import { useTranslation } from 'react-i18next';

import useTable from './hooks/useTable';
import TableView from './view';

/**
 * Table — _fragments reusable table
 *
 * @param {string}   id                 - Unique key for localStorage column-order persistence
 * @param {Array}    columns            - Column configuration (see column shape below)
 * @param {Array}    data               - Row data
 * @param {boolean}  loading            - Show skeleton loading rows
 * @param {number}   loadingRows        - Number of skeleton rows (default 4)
 * @param {string}   emptyMessage       - Text shown when data is empty
 * @param {string}   emptySubtitle      - Subtitle shown below emptyMessage
 * @param {node}     emptyIcon          - Custom icon for empty state
 * @param {boolean}  fullWidth          - Remove border-radius (full-width mode)
 * @param {boolean}  contained          - Add border + border-radius (contained mode)
 * @param {boolean}  draggable          - Allow column reorder via drag (default true)
 * @param {boolean}  enablePagination   - Show pagination bar
 * @param {number}   pages              - Total page count
 * @param {number}   records            - Total record count
 * @param {number}   page               - Current page (1-based)
 * @param {Function} onSelectPage       - Called with new page number
 * @param {Function} onRowClick         - Called with row data on click
 * @param {Function|string} rowClassName - Extra class for each row
 * @param {Function} actionsComponent   - (item) => ReactNode — renders sticky actions column at end
 * @param {string}   className          - Extra class on the container
 *
 * Column shape:
 *   title          string|node   — Header label
 *   field          string        — Data key (used for default render + localStorage key)
 *   id             string        — Explicit key override for localStorage (prefer over field)
 *   render         (item) => any — Return a string/value; wrapped in Text.span automatically
 *   customRender   (item) => node — Return full JSX; overrides render
 *   align          'left'|'center'|'right'
 *   className      string
 *   minWidth       number|string — e.g. 120 or '120px'
 *   maxWidth       number|string
 *   grow           number 1–10   — Proportional fr growth; columns with grow fill remaining space
 *   stickyAtStart  boolean       — Horizontally sticky on left edge
 *   stickyAtEnd    boolean       — Horizontally sticky on right edge (only one at a time)
 *   allowReorder   boolean       — Override per-column draggability (default: true)
 *   loadingWidth   number        — Skeleton width in px
 */
export default function Table({
  id,
  columns = [],
  data = [],
  loading = false,
  loadingRows = 4,
  emptyMessage,
  emptySubtitle,
  emptyIcon,
  fullWidth = false,
  contained = false,
  draggable = true,
  enablePagination = false,
  pages = 0,
  records = 0,
  page = 1,
  onSelectPage,
  onRowClick,
  rowClassName,
  actionsComponent,
  /** px offset from viewport top when sticky (e.g. 61 for a 61px fixed nav). Default: 0 */
  headerTop = 0,
  className = '',
}) {
  const { t } = useTranslation('fragments');
  const tableState = useTable({ id, columns, draggable });

  return (
    <TableView
      {...tableState}
      data={data}
      loading={loading}
      loadingRows={loadingRows}
      emptyMessage={emptyMessage ?? t('table.emptyMessage')}
      emptySubtitle={emptySubtitle}
      emptyIcon={emptyIcon}
      fullWidth={fullWidth}
      contained={contained}
      draggable={draggable}
      enablePagination={enablePagination}
      pages={pages}
      records={records}
      page={page}
      onSelectPage={onSelectPage}
      onRowClick={onRowClick}
      rowClassName={rowClassName}
      actionsComponent={actionsComponent}
      headerTop={headerTop}
      className={className}
    />
  );
}

export { Table };
